import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import storage from "../utils/storage";
import axiosInstance from "../api/axiosConfig";

const ProductContext = createContext();

// Counter to ensure unique IDs even when called rapidly
let productIdCounter = 0;

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const { user } = useAuth();

  // Initial load: fetch from API first, then fall back to any cached
  // rows in localStorage so the UI stays snappy across reloads.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/products");
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.products || [];
        // Normalise so downstream pages that expect `id` keep working
        // alongside the API's `_id`.
        const normalised = list.map((p) => ({
          ...p,
          id: p._id || p.id,
        }));
        if (!cancelled) {
          setProducts(normalised);
          storage.setJSON("products", normalised);
        }
      } catch (e) {
        // Fall back to cached copy on network failure
        const cached = storage.getJSON("products", []);
        if (!cancelled && Array.isArray(cached)) setProducts(cached);
        console.warn("ProductContext: API fetch failed, using cache:", e?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Save products to localStorage whenever they change
  useEffect(() => {
    if (products.length > 0) {
      storage.setJSON("products", products);
    }
  }, [products]);

  // Generate a unique ID that's guaranteed to be different even in rapid succession
  const generateUniqueId = () => {
    const timestamp = Date.now();
    const counter = ++productIdCounter;
    return `${timestamp}-${counter}`;
  };

  const getProduct = (id) => {
    if (!id || !Array.isArray(products)) return null;
    return products.find(
      (product) => product.id && product.id.toString() === id.toString()
    );
  };

  const addProduct = (productData) => {
    if (!productData) return null;

    const newProduct = {
      ...productData,
      id: generateUniqueId(),
      createdAt: new Date().toISOString(),
    };

    setProducts((prevProducts) => [...prevProducts, newProduct]);
    return newProduct;
  };

  const updateProduct = (id, updatedData) => {
    if (!id || !updatedData || !Array.isArray(products)) return null;

    const updatedProducts = products.map((product) =>
      product.id && product.id.toString() === id.toString()
        ? { ...product, ...updatedData, updatedAt: new Date().toISOString() }
        : product
    );

    setProducts(updatedProducts);
    return updatedProducts.find(
      (product) => product.id && product.id.toString() === id.toString()
    );
  };

  const deleteProduct = (id) => {
    if (!id || !Array.isArray(products)) return;

    setProducts(
      products.filter(
        (product) => product.id && product.id.toString() !== id.toString()
      )
    );
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        getProduct,
        addProduct,
        updateProduct,
        deleteProduct,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProduct must be used within a ProductProvider");
  }
  return context;
};
